import { Container, Graphics, type Text } from 'pixi.js';
import gsap from 'gsap';
import { audio } from '../../app/audio';
import { PALETTE } from './palette';
import { makeText, STYLE } from './text';

export interface ButtonOptions {
  label: string;
  width?: number;
  height?: number;
  onPress?: () => void;
  disabled?: boolean;
  /** Small tag drawn after the label, e.g. "soon". */
  tag?: string;
  variant?: 'gold' | 'ghost';
  /** Label size; long choice labels use a smaller one. */
  fontSize?: number;
}

/** A parchment-and-gold button. Hover lifts, press dips, disabled greys. */
export class Button extends Container {
  private readonly bg = new Graphics();
  private readonly glow = new Graphics();
  private glowTween: gsap.core.Tween | null = null;
  private readonly labelText: Text;
  private readonly w: number;
  private readonly h: number;
  private readonly variant: 'gold' | 'ghost';
  private disabled: boolean;

  constructor(opts: ButtonOptions) {
    super({ label: `button:${opts.label}` });
    this.w = opts.width ?? 360;
    this.h = opts.height ?? 72;
    this.variant = opts.variant ?? 'gold';
    this.disabled = opts.disabled ?? false;
    this.glow.roundRect(-9, -9, this.w + 18, this.h + 18, 18).stroke({ color: PALETTE.goldBright, width: 10, alpha: 0.35 });
    this.glow.roundRect(-4, -4, this.w + 8, this.h + 8, 13).stroke({ color: PALETTE.goldBright, width: 4, alpha: 0.9 });
    this.glow.alpha = 0;
    this.addChild(this.glow, this.bg);
    this.paint(0);

    const label = makeText(opts.label, { ...STYLE.display(opts.fontSize ?? 30), fill: this.variant === 'gold' ? PALETTE.ink : PALETTE.parchment });
    label.anchor.set(0.5);
    label.position.set(this.w / 2, this.h / 2 + 2);
    this.addChild(label);
    this.labelText = label;

    if (opts.tag) {
      const tag = makeText(opts.tag, { ...STYLE.mono(16), fill: this.variant === 'gold' ? PALETTE.ink : PALETTE.parchmentDim });
      tag.anchor.set(1, 0.5);
      tag.alpha = 0.7;
      tag.position.set(this.w - 16, this.h / 2 + 2);
      this.addChild(tag);
    }

    this.eventMode = this.disabled ? 'none' : 'static';
    this.cursor = 'pointer';
    this.alpha = this.disabled ? 0.45 : 1;
    this.pivot.set(this.w / 2, this.h / 2);

    this.on('pointerover', () => this.hover(true));
    this.on('pointerout', () => this.hover(false));
    this.on('pointerdown', () => gsap.to(this.scale, { x: 0.97, y: 0.97, duration: 0.06 }));
    this.on('pointerup', () => gsap.to(this.scale, { x: 1.03, y: 1.03, duration: 0.1 }));
    this.on('pointerupoutside', () => this.hover(false));
    this.on('pointertap', () => {
      audio().play('ui-click');
      opts.onPress?.();
    });
    this.on('destroyed', () => this.glowTween?.kill());
  }

  setLabel(text: string): void {
    this.labelText.text = text;
  }

  /** A pulsing halo: "this is what you do next". */
  setGlow(on: boolean): void {
    if (on === (this.glowTween !== null)) return;
    if (!on) {
      this.glowTween?.kill();
      this.glowTween = null;
      gsap.to(this.glow, { alpha: 0, duration: 0.15, overwrite: true });
      return;
    }
    this.glow.alpha = 0.3;
    this.glowTween = gsap.to(this.glow, { alpha: 1, duration: 0.55, yoyo: true, repeat: -1, ease: 'sine.inOut', overwrite: true });
  }

  private hover(on: boolean): void {
    gsap.to(this.scale, { x: on ? 1.03 : 1, y: on ? 1.03 : 1, duration: 0.12, ease: 'power2.out' });
    this.paint(on ? 1 : 0);
  }

  private paint(hover: number): void {
    const g = this.bg;
    g.clear();
    if (this.variant === 'gold') {
      g.roundRect(0, 0, this.w, this.h, 10).fill(hover ? PALETTE.goldBright : PALETTE.gold);
      g.roundRect(3, 3, this.w - 6, this.h - 6, 8).stroke({ color: PALETTE.ink, width: 2, alpha: 0.35 });
    } else {
      g.roundRect(0, 0, this.w, this.h, 10).fill({ color: PALETTE.parchment, alpha: hover ? 0.12 : 0.06 });
      g.roundRect(0, 0, this.w, this.h, 10).stroke({ color: PALETTE.gold, width: 2, alpha: hover ? 1 : 0.6 });
    }
  }
}
